import {Injectable} from '@angular/core';
import {Buffer} from 'buffer';
import {Observable, Observer} from 'rxjs';

export type FileLoadErrorType = 'not.image' | 'could.not.extract';
export type FileFormat = 'IMG' | 'XLS' | 'PDF';

export interface FileLoadError {
  message: string;
  key: FileLoadErrorType;
  params?: any;
}

const contentTypeToExtension: any = {
  // Document Files
  'application/pdf': '.pdf',
  'application/msword': '.doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
  'application/vnd.ms-excel': '.xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
  'application/vnd.ms-powerpoint': '.ppt',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': '.pptx',
  'text/plain': '.txt',
  'application/rtf': '.rtf',

  // Image Files
  'image/jpeg': '.jpg',
  'image/png': '.png',
  'image/gif': '.gif',
  'image/bmp': '.bmp',
  'image/webp': '.webp',
  'image/svg+xml': '.svg',
  'image/tiff': '.tif',
  'image/x-icon': '.ico',

  // Audio Files
  'audio/mpeg': '.mp3',
  'audio/wav': '.wav',
  'audio/ogg': '.ogg',
  'audio/x-aac': '.aac',
  'audio/x-wav': '.wav',

  // Video Files
  'video/mp4': '.mp4',
  'video/x-msvideo': '.avi',
  'video/mpeg': '.mpeg',
  'video/ogg': '.ogv',
  'video/webm': '.webm',
  'video/3gpp': '.3gp',
  'video/x-matroska': '.mkv',

  // Compressed Files
  'application/zip': '.zip',
  'application/x-rar-compressed': '.rar',
  'application/gzip': '.gz',
  'application/x-7z-compressed': '.7z',
  'application/x-tar': '.tar',

  // Web Files
  'text/html': '.html',
  'text/css': '.css',
  'application/javascript': '.js',
  'application/json': '.json',
  'application/xml': '.xml',
  'text/xml': '.xml',

  // Font Files
  'font/ttf': '.ttf',
  'font/woff': '.woff',
  'font/woff2': '.woff2',
  'application/vnd.ms-fontobject': '.eot',
  'font/otf': '.otf',

  // Executable Files
  'application/octet-stream': '.exe',
  'application/x-msdownload': '.exe',
  'application/x-sh': '.sh',

  // Other Common File Types
  'application/x-httpd-php': '.php',
  'application/java-archive': '.jar',
  'application/vnd.android.package-archive': '.apk',
  'application/x-iso9660-image': '.iso',
};

/**
 * An utility service for data.
 */
@Injectable({
  providedIn: 'root',
})
export class DataUtils {
  /**
   * Method to find the byte size of the string provides
   */
  byteSize(base64String: string): string {
    return this.formatAsBytes(this.size(base64String));
  }

  getContentTypeToExtension(contentType: string): string {
    return contentTypeToExtension[contentType] ?? '.' + contentType;
  }

  /**
   * Method to open file
   */
  openFile(data: string, contentType: string | null | undefined, fileName?: string): void {
    contentType = contentType ?? '';
    const byteCharacters = Buffer.from(data, 'base64').toString('binary');
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], {type: contentType});

    const link = document.createElement('a');
    const fileURL = window.URL.createObjectURL(blob);
    link.href = fileURL;
    if (fileName) link.download = fileName;
    link.click();

    window.URL.revokeObjectURL(fileURL); // Revoke the object URL to free memory
  }

  /**
   * Sets the base 64 data & file type of the 1st file on the event (event.target.files[0]) in the passed entity object
   * and returns an observable.
   *
   * @param event the object containing the file (at event.target.files[0])
   * @param editForm the form where the input field is located
   * @param field the field name to set the file's 'base 64 data' on
   * @param isImage boolean representing if the file represented by the event is an image
   * @returns an observable that loads file to form field and completes if successful
   *      or returns error as FileLoadError on failure
   */
  loadFileToForm(event: Event, editForm: any, field: string, isImage: boolean, isExcel: boolean, useBase64: boolean = true): Observable<void> {
    return new Observable((observer: Observer<void>) => {
      const eventTarget: HTMLInputElement | null = event.target as HTMLInputElement | null;
      let isValid: boolean = true;
      if (eventTarget?.files?.[0]) {
        const file: File = eventTarget.files[0];
        // if (
        //   !file.type.startsWith('image/') &&
        //   file.type != 'application/pdf' &&
        //   file.type != 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' &&
        //   file.type != 'text/csv' &&
        //   file.type != 'application/vnd.ms-excel' &&
        //   file.type.startsWith('text/')
        // ) {
        //   const error: FileLoadError = {
        //     message: `File was expected to be an pdf/excel file`,
        //     key: 'could.not.extract',
        //     params: {fileType: file.type},
        //   };
        //   isValid = false;
        //   observer.error(error);
        // }
        // if (
        //   !isExcel &&
        //   (file.type == 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        //     file.type == 'text/csv' ||
        //     file.type == 'application/vnd.ms-excel')
        // ) {
        //   const error: FileLoadError = {
        //     message: `File was expected to be an excel file`,
        //     key: 'could.not.extract',
        //     params: {fileType: file.type},
        //   };
        //   isValid = false;
        //   observer.error(error);
        // }
        if (isValid) {
          const fieldContentType: string = field + 'ContentType';
          const fieldName: string = field + 'Name';
          if (useBase64) {
                      this.toBase64(file, (base64Data: string) => {
            editForm[field + 'File'] = base64Data;
            editForm[fieldName] = file.name;
            editForm[fieldContentType] = file.type;
            editForm[field + 'MimeType'] = file.type;
            observer.next();
            observer.complete();
          });
          } else{
            // Blob mode (new default behavior)
            editForm[`${field}File`] = file; // Store File object directly
            editForm[fieldName] = file.name;
            editForm[fieldContentType] = file.type;
            observer.next();
            observer.complete();
          }

        }
      } else {
        const error: FileLoadError = {
          message: 'Could not extract file',
          key: 'could.not.extract',
          params: {event},
        };
        observer.error(error);
      }
    });
  }

  public GroupBy(items: any[], key: string): {[key: string]: any[]} {
    return items.reduce((acc, item) => {
      const group = item[key];
      acc[group] = acc[group] || [];
      acc[group].push(item);
      return acc;
    }, {});
  }

  /**
   * Method to convert the file to base64
   */
  private toBase64(file: File, callback: (base64Data: string) => void): void {
    const fileReader: FileReader = new FileReader();
    fileReader.onload = (e: ProgressEvent<FileReader>) => {
      if (typeof e.target?.result === 'string') {
        const base64Data: string = e.target.result.substring(e.target.result.indexOf('base64,') + 'base64,'.length);
        callback(base64Data);
      }
    };
    fileReader.readAsDataURL(file);
  }

  private endsWith(suffix: string, str: string): boolean {
    return str.includes(suffix, str.length - suffix.length);
  }

  private paddingSize(value: string): number {
    if (this.endsWith('==', value)) {
      return 2;
    }
    if (this.endsWith('=', value)) {
      return 1;
    }
    return 0;
  }

  private size(value: string): number {
    return (value.length / 4) * 3 - this.paddingSize(value);
  }

  private formatAsBytes(size: number): string {
    return size.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' bytes'; // NOSONAR
  }
}
export function isEmptyObject(obj: any): boolean {
  return obj && Object.keys(obj).length === 0 && obj.constructor === Object;
}
